import { useCallback, useMemo, useState } from 'react';

import { PROMPT_INSTRUCTIONS_ACTION } from 'src/components/PromptInstructions';
import { getCreatedResourceMessage, hasResourcesWithUploadId } from 'src/data/performance';
import { useNotificationActions } from 'src/store';

export const usePromptFilesSupport = () => {
  const notifications = useNotificationActions();

  const [uploadingImages, setUploadingImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  const hasUploadingResources = useMemo(
    () => hasResourcesWithUploadId([...uploadingImages, ...uploadingDocuments]),
    [uploadingImages, uploadingDocuments]
  );

  const insertImage = useCallback(
    (id, data, randomId) => {
      if (!randomId) {
        setUploadingImages((prev) => {
          if (!prev.find((image) => image.id === id)) {
            return [...prev, { id, ...data }];
          }
          return prev;
        });
      } else {
        setUploadingImages((prev) => {
          const index = prev.findIndex((image) => image.id === randomId);
          const updatedUploadedFile = { id, ...data };

          if (index > -1) {
            const updatedUploadingImages = [...prev];
            updatedUploadingImages[index] = updatedUploadedFile;
            return updatedUploadingImages;
          } else {
            return [...prev, updatedUploadedFile];
          }
        });
      }
    },
    [setUploadingImages]
  );

  const handleUploadImage = useCallback(
    (isUploadingImage, randomId) => {
      setIsUploadingImage(isUploadingImage);
      if (isUploadingImage && randomId) {
        setUploadingImages((prev) => [...prev, { id: randomId }]);
      }
    },
    [setIsUploadingImage, setUploadingImages]
  );

  const handleImageUploadSuccess = useCallback(
    (image) => {
      notifications.displaySuccess(getCreatedResourceMessage(image));
    },
    [notifications]
  );

  const insertDocument = useCallback(
    (id, data, randomId) => {
      if (!randomId) {
        setUploadingDocuments((prev) => {
          if (!prev.find((doc) => doc.id === id)) {
            return [...prev, { id, ...data }];
          }
          return prev;
        });
      } else {
        setUploadingDocuments((prev) => {
          const index = prev.findIndex((doc) => doc.id === randomId);
          const updatedUploadedFile = { id, ...data };

          if (index > -1) {
            const updatedUploadingDocuments = [...prev];
            updatedUploadingDocuments[index] = updatedUploadedFile;
            return updatedUploadingDocuments;
          } else {
            return [...prev, updatedUploadedFile];
          }
        });
      }
    },
    [setUploadingDocuments]
  );

  const handleUploadDocument = useCallback(
    (isUploadingDocument, data) => {
      setIsUploadingDocument(isUploadingDocument);
      if (isUploadingDocument && data?.randomId) {
        setUploadingDocuments((prev) => [
          ...prev,
          { id: data?.randomId, label: data?.file?.name, contentType: data?.contentType },
        ]);
      }
    },
    [setIsUploadingDocument, setUploadingDocuments]
  );

  const handleDocumentUploadSuccess = useCallback(
    (document) => {
      notifications.displaySuccess(getCreatedResourceMessage(document));
    },
    [notifications]
  );

  const handlePromptInstructionsAction = useCallback(
    (action, data) => {
      switch (action) {
        case PROMPT_INSTRUCTIONS_ACTION.insertImage: {
          const { id, ...restProps } = data.item;
          insertImage(id, restProps, data.randomId);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertDocument:
        case PROMPT_INSTRUCTIONS_ACTION.insertAudio:
        case PROMPT_INSTRUCTIONS_ACTION.insertVideo: {
          const { id, ...restProps } = data.item;
          insertDocument(id, restProps, data.randomId);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removePromptImage: {
          setUploadingImages((prev) => prev.filter((image) => image.id !== data.itemId));
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removeUploadingImage: {
          setUploadingImages((prev) => prev.filter((image) => image.id !== data.itemId));
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removePromptDocument: {
          setUploadingDocuments((prev) => prev.filter((document) => document.id !== data.itemId));
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removeUploadingDocument: {
          setUploadingDocuments((prev) => prev.filter((document) => document.id !== data.itemId));
          break;
        }

        default:
          break;
      }
    },
    [insertDocument, insertImage, setUploadingDocuments, setUploadingImages]
  );

  const handleDisplayError = useCallback(
    (id, error) => {
      notifications.displayError(error);
    },
    [notifications]
  );

  return {
    // "images"
    uploadingImages,
    isUploadingImage,
    handleUploadImage,
    handleImageUploadSuccess,
    // "document"
    uploadingDocuments,
    isUploadingDocument,
    handleUploadDocument,
    handleDocumentUploadSuccess,
    // "common" handlers
    hasUploadingResources,
    handlePromptInstructionsAction,
    handleDisplayError,
  };
};
